<template>
    <div class="flex-page-content flex-container">
        <div class="flex-body body-scroll">
            <div class="container-fluid">
                <div class="container-centered-m py-4">

                    <trust-browser-prompt v-if="showTrustPrompt" :email="sessionEmail" @done="goNext" />

                    <div v-if="! showTrustPrompt" v-show="! managerLookupOpen">
                        <h1 class="mb-1 text-center">
                            2-Step Verification
                        </h1>

                        <div class="mb-4 text-center">
                            <span class="text-muted">
                                {{ sessionEmail }}
                            </span>
                        </div>

                        <p>
                            This extra step shows it's really you trying to sign in.
                        </p>

                        <div v-if="sessionMethod == 'email'" class="pt-2 pb-1 border-top">
                            <h3 class="mb-0">
                                Check your email
                            </h3>
                            <form @submit.prevent="onSubmit" novalidate class="mui-form">
                                <br>
                                <form-errors :errors="formErrors"/>

                                <div class="pt-1">
                                    <p>
                                        We've sent a verification code to <strong>{{ sessionEmail }}</strong>.
                                        <br>
                                        Enter the code here:
                                    </p>

                                    <div class="form-row">
                                        <div class="col-6">
                                            <form-input placeholder="Verification Code" v-model="formData.verification_code" :errors="errors.verification_code" type="text" inputmode="numeric" maxlength="6" autocomplete="one-time-code" name="code" />
                                        </div>
                                    </div>

                                    <div class="pt-2">
                                        <button type="submit" class="btn btn-primary mr-2 mb-1">Sign In</button>
                                        <span class="btnlike mb-1">
                                            Didn't get the code?&nbsp; <a href @click.prevent="sendEmailCode()">Re-send email</a>
                                        </span>
                                    </div>
                                </div>
                            </form>
                        </div>

                        <div class="accordion" id="mfaAccordion" v-if="sessionMethod == 'device' || sessionMethod == 'mfa'">

                            <mfa-step
                                :methods="sessionMethod == 'mfa' ? login.mfaMethods : ['device']"
                                :mobile-devices="login.mobileDevices"
                                :device-push-sent="login.devicePushSent"
                                :push-number="login.pushNumber"
                                :email="sessionEmail"
                                :complete="completeLogin"
                                v-model:open-section="openSection"
                            />

                            <!-- Section: Email Code -->
                            <section class="border-top py-2" v-if="sessionMethod == 'device'">
                                <section-toggle controls="mfaEmailCode" :open="openSection == 'email'" @toggle="openSection = openSection == 'email' ? '' : 'email'">
                                    Get a code via email
                                </section-toggle>
                                <div id="mfaEmailCode" v-show="openSection == 'email'">

                                    <!-- I'm using a headless accordion here to fake an asynchronous call -->

                                    <div class="accordion" id="mfaEmailCodeSteps">

                                        <div data-parent="#mfaEmailCodeSteps" class="collapse show no-transition" v-if="!emailSent">
                                            <div class="pt-1">
                                                <p>
                                                    We'll send a verification code to <strong>{{ sessionEmail }}</strong>.
                                                </p>
                                                <p>
                                                    <a class="btn btn-primary" href role="button" @click.prevent="sendEmailCode()">Send Code</a>
                                                </p>
                                            </div>
                                        </div>

                                        <div data-parent="#mfaEmailCodeSteps" class="collapse show no-transition" v-if="emailSent">
                                            <form @submit.prevent="onSubmit" novalidate class="mui-form">
                                                <form-errors :errors="formErrors"/>

                                                <div class="pt-1">
                                                    <p>
                                                        We've sent a verification code to <strong>{{ sessionEmail }}</strong>.
                                                        <br>
                                                        Enter the code here:
                                                    </p>

                                                    <div class="form-row">
                                                        <div class="col-6">
                                                            <form-input placeholder="Verification Code" v-model="formData.verification_code" :errors="errors.verification_code" type="text" inputmode="numeric" maxlength="6" />
                                                        </div>
                                                    </div>

                                                    <div class="pt-2">
                                                        <button type="submit" class="btn btn-primary mr-2 mb-1">Sign In</button>
                                                        <span class="btnlike mb-1">
                                                            Didn't get the code?&nbsp; <a href @click.prevent="sendEmailCode()">Re-send email</a>
                                                        </span>
                                                    </div>
                                                </div>
                                            </form>
                                        </div>

                                    </div>

                                </div>
                            </section>

                        </div>

                        <!-- Button footer -->
                        <div class="border-top pt-1">
                            <div class="row py-2 d-flex align-items-center">
                                <div class="col-12 col-sm-3 text-center text-sm-left">
                                    <router-link :to="{name: 'login'}" class="btn btn-link">Back</router-link>
                                </div>
                                <div class="col-12 col-sm-9 text-center text-sm-right">
                                    <a v-if="sessionMethod != 'mfa'" href class="btn btn-link" @click.prevent="managerLookupOpen = true">I no longer have access to this email address</a>
                                </div>
                            </div>
                        </div>
                    </div>

                    <manager-lookup v-if="managerLookupOpen && ! showTrustPrompt" :is-verified="true" />
                </div>

            </div>
        </div>

    </div>
</template>

<script>
import FormMixin from '@/mixins/Form'
import ManagerLookup from "@/components/ManagerLookup"
import MfaStep from '@/components/Login/MfaStep'
import SectionToggle from '@/components/Login/SectionToggle'
import TrustBrowserPrompt from '@/components/Login/TrustBrowserPrompt'
import { isNativeHost } from '@/native-bridge'
import api from '@/api'
import bus from '@/bus'

export default {
    mixins: [FormMixin],
    components: {ManagerLookup, MfaStep, SectionToggle, TrustBrowserPrompt, },
    mounted() {
        this.$store.dispatch('SET_PAGE_TITLE', 'Login')
        if (!this.$store.state.session) {
            this.$router.push({name: 'login'})
        }


        // Attempt already validated by a trusted-browser cookie: nothing to ask
        if (this.sessionMethod == 'trusted_browser') {
            this.completeLogin({}).catch((errors) => {
                this.$router.push({name: 'login'})
                bus.showError(bus.firstError(errors))
            })
        }
    },
    computed: {
        sessionMethod() {
            return this.login.method
        },
        sessionEmail() {
            return this.login.email
        }
    },
    data() {
        // Captured once: /login/complete replaces the session before the page moves on
        const session = this.$store.state.session || {}
        return {
            login: {
                method: session.login_attempt_verification_method || null,
                email: session.email || '',
                mfaMethods: session.mfa_methods || [],
                mobileDevices: session.mobile_devices || [],
                devicePushSent: !!session.device_push_sent,
                pushNumber: session.push_number || '',
            },
            emailSent: false,
            // The open accordion section; MfaStep opens its first one
            openSection: '',
            showTrustPrompt: false,
            formRules: {
                verification_code: {
                    presence: {allowEmpty: false},
                },
            },
            formData: {
                verification_code: '',
            },
            managerLookupOpen: false,
        }
    },
    methods: {
        onSubmit() {
            if (!this.validate()) {
                return
            }

            this.completeLogin(this.getFormData()).catch((errors) => {
                this.errors = errors
                // if it's a 400, that means it's an invalid code and could be a typo -
                // let them fix it; if it's a 404, it means the session is wrong, and they
                // need to restart
                if (errors.__status__ == 404) {
                    this.$router.push({name: 'login'})
                    bus.showError(bus.firstError(errors))
                } else {
                    this.formErrors = errors.__all__
                }
            })
        },
        completeLogin(payload) {
            return this.$store.dispatch('LOGIN_VALIDATE_EMAIL', payload).then(this.onVerified)
        },
        onVerified(resp) {
            if (resp && resp.offer_trust_browser && !isNativeHost()) {
                this.showTrustPrompt = true
            } else {
                this.goNext()
            }
        },
        goNext() {
            const next = this.$route.query.next
            this.$router.push(next ? decodeURIComponent(next) : '/')
        },
        sendEmailCode() {
            this.$store.dispatch('START_LOADING')
            api.post('/login/email-code', {}).then(() => {
                this.$store.dispatch('STOP_LOADING')
                bus.showInfo('Email code was sent!')
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.$store.dispatch('LOGOUT')
                this.$router.push({name: 'login'})
                bus.showError(bus.firstError(errors))
            })
            this.emailSent = true
        },

    },
}
</script>
